/* @flow */
/* eslint-env es6,browser */

import { apiName, apiVersion, getRandom } from '../utils';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { getUserID } from '../idproviders/idputils';
import {
  BrowserUtil,
  isJSONSupported,
  isMobile,
  isDNT,
  isSecure
} from '../utils/browserutil';
import { logError } from '../utils/logger';
import { allSettledPromise } from '../utils/promiseutils';
import { extend } from '../utils/coreutils';
import {
  PREBID_UID_PUBLINKID,
  PREBID_UID_PUBCOMMON,
  PREBID_UID_LIVERAMP,
  PREBID_UID_CRITEO,
  PREBID_UID_PARRABLEID,
  PREBID_UID_UNIFIEDID
} from '../idproviders/idpconstants';

/**
 * @module bidders/conversant
 * @requires module:logger
 * @requires module:promiseutils
 */
/**
 * @global
 * @description extends {@link hbConfig}
 * @property {string} bidUrl - Bid URL
 */

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */

declare type convReqPos = {
  id: string,
  at: 1,
  site: {
    id: string,
    mobile: 0 | 1
  },
  device: {
    dnt: 0 | 1,
    ua: string
  },
  imp: Array<{
    id: string,
    secure: 0 | 1,
    displaymanager: string,
    displaymanagerver: string,
    banner: {
      w: number,
      h: number
    }
  }>
};

declare type convBidRequestCache = {
  impid: {
    [key: string]: string
  }
};

/**
 * @description Implements functionality for conversant header bidding
 * @augments _bidbase
 * @requires module:utils
 * @requires module:jsonp
 */
class _conversant extends bidbase {
  _bidRequest: convBidRequestCache;

  _config: conversantBidConfig;

  constructor(name: string) {
    super(name);
    this._mandatoryConfigs = ['bidUrl'];
    this._bidRequest = {
      impid: {}
    };
    this._isSupported = isJSONSupported() && isFetchSupported();
  }

  /**
   * @description Process RTB bid response. Response Format:
   * _bidResponses[impid].seatbid[0].bid[0].price - ecpm
   * _bidResponses[impid].seatbid[0].bid[0].adm - ad creative
   * _bidResponses[impid].seatbid[0].bid[0].nurl - impression recording pixel
   * @param {Array<string>} posList - array of positions
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    posList.forEach(pos => {
      const impid = this._bidRequest.impid[pos];
      const resp: RTBBidResponse = this._bidResponses[impid];
      if (
        !this._isValidRTBResponse(resp) ||
        impid !== resp.seatbid[0].bid[0].impid
      ) {
        return;
      }
      // eslint-disable-next-line prefer-destructuring
      const res = resp.seatbid[0].bid[0];
      if (res && res.price && res.nurl && res.adm) {
        this._handleBidPosData(ret, pos, res.price, impid, res.adm, res.nurl);
      }
    });
    return ret;
  }

  _getBid(pos: string): Promise<boolean> {
    const url = this._config.bidUrl;
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: pos
    };
    const params = extend(true, {}, this._buildReqParams(pos), {
      // similar to prebid (openRTB)
      regs: { ext: this._getPrivacyString() }
    });
    const req = {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(params)
    };
    return fetchJSON(url, opt, req).then(json => {
      this._bidResponses[this._bidRequest.impid[pos]] = json;
      return true;
    });
  }

  /**
   * Build request params in RTB format
   *
   * @param {string} pos - Ad position
   * @returns {object} - Request URL info for given position
   * @access private
   */
  _buildReqParams(pos: string): convReqPos {
    // generate unique request id
    this._bidRequest.impid[pos] = getRandom();

    // build params for bid request
    const params = {
      id: getRandom(),
      site: {
        id: this._config.posTokenMap[pos].id,
        mobile: isMobile() ? 1 : 0
      },
      device: {
        ua: BrowserUtil.getUserAgent(),
        dnt: isDNT() ? 1 : 0
      },
      imp: [
        {
          id: this._bidRequest.impid[pos],
          banner: {
            w: this._config.posTokenMap[pos].width,
            h: this._config.posTokenMap[pos].height
          },
          secure: isSecure() ? 1 : 0,
          displaymanager: apiName(),
          displaymanagerver: apiVersion()
        }
      ],
      at: 1,
      user: {}
    };
    const userExt = {};
    const pubcid = getUserID(PREBID_UID_PUBCOMMON);
    if (pubcid && pubcid.length) {
      userExt.fpc = pubcid;
    }

    const eids = this._getRTBUserEids([
      PREBID_UID_PUBLINKID,
      PREBID_UID_LIVERAMP,
      PREBID_UID_CRITEO,
      PREBID_UID_PARRABLEID,
      PREBID_UID_UNIFIEDID
    ]);
    if (eids.user && eids.user.eids && eids.user.eids.length) {
      userExt.eids = eids.user.eids;
    }

    // eslint-disable-next-line compat/compat
    if (Object.entries(userExt).length) {
      params.user = { ext: userExt };
    }
    return params;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};

    // Create promise array with all bid requests
    const pArray = posList.map(pos => this._getBid(pos));
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise errors
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
    // return Promise.resolve(ret);
  }
}

const conversant: _conversant = new _conversant('conversant');
export default conversant;
